import { CircularProgress, Grid } from '@mui/material'
import InfoBox from './InfoBox'
import { useAppTranslation } from 'i18n/hooks'
import { string } from 'prop-types'
import { useLatestAuctionStats } from './useLatestAuctionStats'
import styled from 'styled-components'
import Text, { TextTypes } from 'Components/Ui-Kits/Text'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons'
import { Colors } from 'constants/colors'
import { useSetQuery } from 'utils/useSetQuery'
import { formatAmount } from 'utils/formatAmount'

export const Stats = ({ auctionId }) => {
  const {
    totalPublishedProducts,
    itemsWithBidsCount,
    bidCount,
    customersWithBidsCount,
    winningPreBidsSum,
    winningLiveBidsSum,
    holmastoStats,
    customerStats,
    allStats,
    customerDistribution,
    holmastoUnsoldStats,
    customerUnsoldStats,
    allUnsoldStats,
    holmastoSalesTotal,
    buyerCommissionTotal,
    sellerPayoutTotal,
    customerDistributionLoading,
    buyerCommissionLoading,
    loading,
  } = useLatestAuctionStats(auctionId)
  const { t } = useAppTranslation()
  const setQuery = useSetQuery()
  const goToBids = () => {
    setQuery({ auction: auctionId, tab: 'bids', subTab: 'list' })
  }

  return (
    <Grid container spacing={5} alignItems="stretch">
      <Grid item xs={6} alignItems="center">
        <InfoBox
          loading={loading}
          className={'card_spacing'}
          header={t('auctionTotals')}
          data={[
            {
              label: t('itemsWithBids'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>{[itemsWithBidsCount, totalPublishedProducts].join(' / ')}</Text>
                  <Icon icon={faSquareArrowUpRight} onClick={goToBids} />
                </Value>
              ),
            },
            {
              label: t('sumOfWinningPreBids'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>{formatAmount(winningPreBidsSum)}</Text>
                  <Icon icon={faSquareArrowUpRight} onClick={goToBids} />
                </Value>
              ),
            },
            {
              label: t('sumOfWinningLiveBids'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>{formatAmount(winningLiveBidsSum)}</Text>
                  <Icon icon={faSquareArrowUpRight} onClick={goToBids} />
                </Value>
              ),
            },
            {
              label: t('buyerCommissionTotal'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>
                    {buyerCommissionLoading ? <CircularProgress size={14} /> : formatAmount(buyerCommissionTotal)}
                  </Text>
                </Value>
              ),
            },
            {
              label: t('sellerPayoutTotal'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>{formatAmount(sellerPayoutTotal)}</Text>
                </Value>
              ),
            },
            {
              label: t('holmastoSalesTotal'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>{formatAmount(holmastoSalesTotal)}</Text>
                </Value>
              ),
            },
          ]}
        />
      </Grid>
      <Grid item xs={6} alignItems="center">
        <InfoBox
          loading={loading}
          className={'card_spacing'}
          header={t('customerDistribution')}
          data={[
            {
              label: t('finland'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>
                    {customerDistributionLoading ? <CircularProgress size={14} /> : customerDistribution.fin}
                  </Text>
                </Value>
              ),
            },
            {
              label: t('europe'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>
                    {customerDistributionLoading ? <CircularProgress size={14} /> : customerDistribution.eu}
                  </Text>
                </Value>
              ),
            },
            {
              label: t('otherCountries'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>
                    {customerDistributionLoading ? <CircularProgress size={14} /> : customerDistribution.other}
                  </Text>
                </Value>
              ),
            },
            {
              label: t('bidsTotal'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>{bidCount}</Text>
                  <Icon icon={faSquareArrowUpRight} onClick={goToBids} />
                </Value>
              ),
            },
            {
              label: t('uniqueCustomersWithBids'),
              value: (
                <Value>
                  <Text type={TextTypes.BODY_20}>{customersWithBidsCount}</Text>
                  <Icon icon={faSquareArrowUpRight} onClick={goToBids} />
                </Value>
              ),
            },
          ]}
        />
      </Grid>
      <Grid item xs={6} alignItems="center">
        <InfoBox
          loading={loading}
          className={'card_spacing'}
          header={t('soldItems')}
          data={[
            {
              label: t('holmastoItems'),
              value: (
                <ValueWithSmallText
                  value={`${holmastoStats.soldCount} / ${holmastoStats.totalCount}`}
                  smallText={`${t('avgPrice')} ${formatAmount(holmastoStats.avgPrice.toFixed(0))}`}
                />
              ),
            },
            {
              label: t('customerItems'),
              value: (
                <ValueWithSmallText
                  value={`${customerStats.soldCount} / ${customerStats.totalCount}`}
                  smallText={`${t('avgPrice')} ${formatAmount(customerStats.avgPrice.toFixed(0))}`}
                />
              ),
            },
            {
              label: t('allItems'),
              value: (
                <ValueWithSmallText
                  value={`${allStats.soldCount} / ${allStats.totalCount}`}
                  smallText={`${t('avgPrice')} ${formatAmount(allStats.avgPrice.toFixed(0))}`}
                />
              ),
            },
          ]}
        />
      </Grid>
      <Grid item xs={6} alignItems="center">
        <InfoBox
          loading={loading}
          className={'card_spacing'}
          header={t('notSoldItems')}
          data={[
            {
              label: t('holmastoItems'),
              value: (
                <ValueWithSmallText
                  value={`${holmastoUnsoldStats.soldCount} / ${holmastoUnsoldStats.totalCount}`}
                  smallText={`${t('startingAvgPrice')} ${formatAmount(holmastoUnsoldStats.avgPrice.toFixed(0))}`}
                />
              ),
            },
            {
              label: t('customerItems'),
              value: (
                <ValueWithSmallText
                  value={`${customerUnsoldStats.soldCount} / ${customerUnsoldStats.totalCount}`}
                  smallText={`${t('startingAvgPrice')} ${formatAmount(customerUnsoldStats.avgPrice.toFixed(0))}`}
                />
              ),
            },
            {
              label: t('allItems'),
              value: (
                <ValueWithSmallText
                  value={`${allUnsoldStats.soldCount} / ${allUnsoldStats.totalCount}`}
                  smallText={`${t('startingAvgPrice')} ${formatAmount(allUnsoldStats.avgPrice.toFixed(0))}`}
                />
              ),
            },
          ]}
        />
      </Grid>
    </Grid>
  )
}

function ValueWithSmallText({ value, smallText }) {
  return (
    <>
      <Text style={{ marginRight: '0.5rem' }}>{value}</Text>
      <p
        style={{
          fontSize: '0.7rem',
          fontFamily: 'inherit',
        }}
      >
        {smallText}
      </p>
    </>
  )
}
ValueWithSmallText.propTypes = {
  value: string,
  smallText: string,
}

const Value = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const Icon = styled(FontAwesomeIcon)`
  font-size: 1.25rem;
  margin-left: 1.25rem;
  color: ${Colors.MAROON};
  cursor: pointer;
`

Stats.propTypes = {
  auctionId: string.isRequired,
}
